/**
 * THE LANGUAGE RUNTIME.
 *
 * Wording lives in the tables under src/i18n/messages/ — one row per wording,
 * one column per language — and is compiled by `npm run i18n`. The first-paint
 * tables become resources.gen.ts and are here from the start; the rest become
 * bundles/*.gen.ts, which call `addMessages` when the code that reads them is
 * imported (scripts/i18n.ts says why). Keys are flat and dotted
 * ("nav.settings"), so the key separator is off.
 *
 * WHOSE CHOICE THE LANGUAGE IS. Rachel's decision, 18-09-2026: the ACCOUNT's
 * `displayLang` states it, so signing in applies the account's language
 * (`applyAccountLanguage`). This reverses the workstation-preference rule this
 * file was written to; `docs/DECISIONS.md` records it.
 *
 * AND SINCE 21-09-2026 THE PICKER SAVES. `PUT /v1/user/preference` closed
 * GAPS.md B6: until it existed, a change made at the counter lasted on that
 * terminal and was silently overwritten by `displayLang` at the next sign-in,
 * so anybody who preferred Chinese re-chose it every shift. The picker now
 * writes the account (`app-header.tsx`), and `setLocale` stays the local half
 * of that — it paints immediately and keeps the answer if the save fails.
 *
 * localStorage is therefore a CACHE of the account's answer rather than a
 * second opinion about it: it is what the next first paint reads before any
 * request has returned, which is why it is still written and still per
 * terminal.
 *
 * A storage failure falls back to English rather than breaking the page, and the
 * locale is never healthcare data.
 */
import i18next from "i18next";
import type { DisplayLang } from "@/api/preference";
import { initReactI18next, useTranslation } from "react-i18next";
import { LOCALES, resources, type Locale, type MessageKey } from "./resources.gen";

export { LOCALES, type Locale, type MessageKey };

const STORAGE_KEY = "pharmos.locale";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

function storedLocale(): Locale {
  try {
    const value = typeof localStorage === "undefined" ? null : localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : "en";
  } catch {
    return "en";
  }
}

export const i18n = i18next.createInstance();

void i18n.use(initReactI18next).init({
  resources,
  lng: storedLocale(),
  fallbackLng: "en",
  supportedLngs: [...LOCALES],
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false }, // React escapes
  returnNull: false,
  initAsync: false,
});

/**
 * Registers a bundle's rows — called by `bundles/*.gen.ts` as it is imported,
 * so by the time the code beside it renders, its words are here. Never
 * overwrites: a key lives in one table, and the compiler refuses a second.
 */
export function addMessages(bundle: Readonly<Record<string, Readonly<Record<string, string>>>>): void {
  for (const [locale, messages] of Object.entries(bundle))
    i18n.addResourceBundle(locale, "translation", messages, true, false);
}

function syncDocument(locale: string) {
  if (typeof document !== "undefined") document.documentElement.lang = locale;
}
syncDocument(i18n.language);
i18n.on("languageChanged", syncDocument);

export function setLocale(locale: Locale): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // A private window: the choice lasts for this page only.
  }
  void i18n.changeLanguage(locale);
}

/**
 * The account's `displayLang` as one of ours.
 *
 * The contract types it as a bare string with no enum and no example (GAPS.md
 * B6), so this matches on the language subtag rather than on an exact spelling:
 * `zh`, `zh-HK`, `zh_Hant`, `ZH-hk` are all the Chinese column. A value that
 * names no language we have is null — not English. "We do not recognise this"
 * and "they asked for English" are different facts, and only the first one
 * should leave the terminal's own choice standing.
 */
export function localeFromDisplayLang(displayLang: string | null | undefined): Locale | null {
  const subtag = displayLang?.trim().toLowerCase().split(/[-_]/)[0];
  if (!subtag) return null;
  if (subtag === "zh") return "zh-HK";
  if (subtag === "en") return "en";
  return null;
}

/**
 * Applies the account's language at sign-in. An unrecognised or absent
 * `displayLang` leaves whatever the terminal was reading.
 */
export function applyAccountLanguage(displayLang: string | null | undefined): void {
  const locale = localeFromDisplayLang(displayLang);
  if (locale !== null) setLocale(locale);
}

/**
 * The other direction, for `PUT /v1/user/preference` (21-09-2026).
 *
 * The write side of the contract IS enumerated — `EN` or `ZH` — while the read
 * side is a bare string, so this is narrow where `localeFromDisplayLang` is
 * forgiving, and deliberately: we accept whatever the server says and send
 * back only what it declared it takes.
 *
 * `ZH` IS TRADITIONAL CHINESE, `EN` IS ENGLISH (Rachel, 21-09-2026). The
 * enum has one Chinese and it is the one this pharmacy reads, so `zh-HK` goes
 * out as `ZH` and `localeFromDisplayLang` brings it back as `zh-HK`, with
 * nothing lost. A second Chinese would be a new value beside `ZH`, never a new
 * meaning for it.
 */
export function displayLangOf(locale: Locale): DisplayLang {
  return locale === "zh-HK" ? "ZH" : "EN";
}

/** `const { t, locale } = useT()` — `t` only accepts keys that exist in the table. */
export function useT() {
  const { t, i18n: instance } = useTranslation();
  return { t, locale: isLocale(instance.language) ? instance.language : "en" };
}
